import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../../database/firebase";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Heart,
  Pill,
  Activity,
  Shield,
  Stethoscope,
} from "lucide-react";

const HealthConditions = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    allergies: true,
    chronic: false,
    endocrine: false,
    medications: false,
    surgeries: false,
    restrictions: false,
    emergency: false,
  });

  const [healthData, setHealthData] = useState({
    allergies: [],
    chronicConditions: [],
    endocrineDisorders: [],
    currentMedications: [],
    recentSurgeries: [],
    lifestyleRestrictions: {
      exercise: [],
      diet: [],
      activity: [],
    },
    emergencyInfo: {
      hasEpipen: false,
      primaryPhysician: "",
      lastCheckup: "",
    },
    metadata: {
      lastUpdated: null,
      verifiedByUser: false,
      sharedWithCoach: false,
    },
  });

  // Load existing health data
  useEffect(() => {
    if (user?.healthConditions) {
      setHealthData({
        allergies: user.healthConditions.allergies || [],
        chronicConditions: user.healthConditions.chronicConditions || [],
        endocrineDisorders: user.healthConditions.endocrineDisorders || [],
        currentMedications: user.healthConditions.currentMedications || [],
        recentSurgeries: user.healthConditions.recentSurgeries || [],
        lifestyleRestrictions: user.healthConditions.lifestyleRestrictions || {
          exercise: [],
          diet: [],
          activity: [],
        },
        emergencyInfo: user.healthConditions.emergencyInfo || {
          hasEpipen: false,
          primaryPhysician: "",
          lastCheckup: "",
        },
        metadata: user.healthConditions.metadata || {
          lastUpdated: null,
          verifiedByUser: false,
          sharedWithCoach: false,
        },
      });
    }
  }, [user]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Allergy Management
  const addAllergy = () => {
    setHealthData((prev) => ({
      ...prev,
      allergies: [
        ...prev.allergies,
        {
          type: "food",
          name: "",
          severity: "mild",
          reaction: "",
          managedWith: "",
        },
      ],
    }));
  };

  const updateAllergy = (index, field, value) => {
    setHealthData((prev) => {
      const updated = [...prev.allergies];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, allergies: updated };
    });
  };

  const removeAllergy = (index) => {
    setHealthData((prev) => ({
      ...prev,
      allergies: prev.allergies.filter((_, i) => i !== index),
    }));
  };

  // Chronic Condition Management
  const addChronicCondition = () => {
    setHealthData((prev) => ({
      ...prev,
      chronicConditions: [
        ...prev.chronicConditions,
        {
          name: "",
          type: "",
          diagnosedDate: "",
          severity: "mild",
          management: {
            medication: false,
            diet: false,
            exercise: false,
            monitoringFrequency: "",
          },
          complications: [],
        },
      ],
    }));
  };

  const updateChronicCondition = (index, field, value) => {
    setHealthData((prev) => {
      const updated = [...prev.chronicConditions];
      if (field.includes(".")) {
        const [parent, child] = field.split(".");
        updated[index] = {
          ...updated[index],
          [parent]: { ...updated[index][parent], [child]: value },
        };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return { ...prev, chronicConditions: updated };
    });
  };

  const removeChronicCondition = (index) => {
    setHealthData((prev) => ({
      ...prev,
      chronicConditions: prev.chronicConditions.filter((_, i) => i !== index),
    }));
  };

  // Endocrine Disorder Management
  const addEndocrineDisorder = () => {
    setHealthData((prev) => ({
      ...prev,
      endocrineDisorders: [
        ...prev.endocrineDisorders,
        {
          name: "",
          type: "",
          diagnosedDate: "",
          severity: "mild",
          symptoms: [],
          treatment: [],
        },
      ],
    }));
  };

  const updateEndocrineDisorder = (index, field, value) => {
    setHealthData((prev) => {
      const updated = [...prev.endocrineDisorders];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, endocrineDisorders: updated };
    });
  };

  const removeEndocrineDisorder = (index) => {
    setHealthData((prev) => ({
      ...prev,
      endocrineDisorders: prev.endocrineDisorders.filter((_, i) => i !== index),
    }));
  };

  // Medication Management
  const addMedication = () => {
    setHealthData((prev) => ({
      ...prev,
      currentMedications: [
        ...prev.currentMedications,
        {
          name: "",
          dosage: "",
          frequency: "",
          purpose: "",
          startDate: "",
          notes: "",
        },
      ],
    }));
  };

  const updateMedication = (index, field, value) => {
    setHealthData((prev) => {
      const updated = [...prev.currentMedications];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, currentMedications: updated };
    });
  };

  const removeMedication = (index) => {
    setHealthData((prev) => ({
      ...prev,
      currentMedications: prev.currentMedications.filter((_, i) => i !== index),
    }));
  };

  // Surgery Management
  const addSurgery = () => {
    setHealthData((prev) => ({
      ...prev,
      recentSurgeries: [
        ...prev.recentSurgeries,
        {
          type: "",
          date: "",
          recoveryStatus: "recovering",
        },
      ],
    }));
  };

  const updateSurgery = (index, field, value) => {
    setHealthData((prev) => {
      const updated = [...prev.recentSurgeries];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, recentSurgeries: updated };
    });
  };

  const removeSurgery = (index) => {
    setHealthData((prev) => ({
      ...prev,
      recentSurgeries: prev.recentSurgeries.filter((_, i) => i !== index),
    }));
  };

  // Lifestyle Restrictions
  const addRestriction = (category, value) => {
    if (value.trim()) {
      setHealthData((prev) => ({
        ...prev,
        lifestyleRestrictions: {
          ...prev.lifestyleRestrictions,
          [category]: [...prev.lifestyleRestrictions[category], value.trim()],
        },
      }));
    }
  };

  const removeRestriction = (category, index) => {
    setHealthData((prev) => ({
      ...prev,
      lifestyleRestrictions: {
        ...prev.lifestyleRestrictions,
        [category]: prev.lifestyleRestrictions[category].filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        healthConditions: {
          ...healthData,
          metadata: {
            lastUpdated: new Date().toISOString(),
            verifiedByUser: true,
            sharedWithCoach: healthData.metadata.sharedWithCoach,
          },
        },
        updatedAt: new Date().toISOString(),
      });

      toast.success("Health conditions updated successfully!");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Error updating health conditions:", error);
      toast.error("Failed to update health conditions. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const SectionHeader = ({ icon: Icon, title, section, count }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-blue-600" />
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {count > 0 && (
          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
            {count}
          </span>
        )}
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="w-5 h-5 text-gray-500" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-500" />
      )}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="w-full max-w-4xl mx-auto bg-white shadow-sm">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/profile")}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              <h1 className="text-xl font-bold text-gray-900">
                Health Conditions
              </h1>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Allergies Section */}
          <div className="space-y-3">
            <SectionHeader
              icon={AlertCircle}
              title="Allergies"
              section="allergies"
              count={healthData.allergies.length}
            />
            {expandedSections.allergies && (
              <div className="space-y-3 pl-4">
                {healthData.allergies.map((allergy, index) => (
                  <div
                    key={index}
                    className="p-4 border border-gray-200 rounded-lg space-y-3"
                  >
                    <div className="flex justify-between items-start">
                      <h4 className="text-sm font-medium text-gray-700">
                        Allergy #{index + 1}
                      </h4>
                      <button
                        type="button"
                        onClick={() => removeAllergy(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Type
                        </label>
                        <select
                          value={allergy.type}
                          onChange={(e) =>
                            updateAllergy(index, "type", e.target.value)
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        >
                          <option value="food">Food</option>
                          <option value="medication">Medication</option>
                          <option value="environmental">Environmental</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Name
                        </label>
                        <input
                          type="text"
                          value={allergy.name}
                          onChange={(e) =>
                            updateAllergy(index, "name", e.target.value)
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          placeholder="e.g., peanuts, penicillin"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Severity
                        </label>
                        <select
                          value={allergy.severity}
                          onChange={(e) =>
                            updateAllergy(index, "severity", e.target.value)
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        >
                          <option value="mild">Mild</option>
                          <option value="moderate">Moderate</option>
                          <option value="severe">Severe</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Reaction
                        </label>
                        <input
                          type="text"
                          value={allergy.reaction}
                          onChange={(e) =>
                            updateAllergy(index, "reaction", e.target.value)
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          placeholder="e.g., hives, anaphylaxis"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Managed With
                        </label>
                        <input
                          type="text"
                          value={allergy.managedWith}
                          onChange={(e) =>
                            updateAllergy(index, "managedWith", e.target.value)
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          placeholder="e.g., epinephrine, antihistamines"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addAllergy}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Allergy
                </button>
              </div>
            )}
          </div>

          {/* Chronic Conditions Section */}
          <div className="space-y-3">
            <SectionHeader
              icon={Activity}
              title="Chronic Conditions"
              section="chronic"
              count={healthData.chronicConditions.length}
            />
            {expandedSections.chronic && (
              <div className="space-y-3 pl-4">
                {healthData.chronicConditions.map((condition, index) => (
                  <div
                    key={index}
                    className="p-4 border border-gray-200 rounded-lg space-y-3"
                  >
                    <div className="flex justify-between items-start">
                      <h4 className="text-sm font-medium text-gray-700">
                        Condition #{index + 1}
                      </h4>
                      <button
                        type="button"
                        onClick={() => removeChronicCondition(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Condition Name
                        </label>
                        <input
                          type="text"
                          value={condition.name}
                          onChange={(e) =>
                            updateChronicCondition(
                              index,
                              "name",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          placeholder="e.g., diabetes, hypertension"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Type
                        </label>
                        <input
                          type="text"
                          value={condition.type}
                          onChange={(e) =>
                            updateChronicCondition(
                              index,
                              "type",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          placeholder="e.g., type2, essential"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Diagnosed Date
                        </label>
                        <input
                          type="date"
                          value={condition.diagnosedDate}
                          onChange={(e) =>
                            updateChronicCondition(
                              index,
                              "diagnosedDate",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Severity
                        </label>
                        <select
                          value={condition.severity}
                          onChange={(e) =>
                            updateChronicCondition(
                              index,
                              "severity",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        >
                          <option value="mild">Mild</option>
                          <option value="moderate">Moderate</option>
                          <option value="severe">Severe</option>
                          <option value="controlled">Controlled</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-2">
                          Management
                        </label>
                        <div className="flex flex-wrap gap-3">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={condition.management?.medication}
                              onChange={(e) =>
                                updateChronicCondition(
                                  index,
                                  "management.medication",
                                  e.target.checked
                                )
                              }
                              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">
                              Medication
                            </span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={condition.management?.diet}
                              onChange={(e) =>
                                updateChronicCondition(
                                  index,
                                  "management.diet",
                                  e.target.checked
                                )
                              }
                              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Diet</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={condition.management?.exercise}
                              onChange={(e) =>
                                updateChronicCondition(
                                  index,
                                  "management.exercise",
                                  e.target.checked
                                )
                              }
                              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">
                              Exercise
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addChronicCondition}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Chronic Condition
                </button>
              </div>
            )}
          </div>

          {/* Current Medications Section */}
          <div className="space-y-3">
            <SectionHeader
              icon={Pill}
              title="Current Medications"
              section="medications"
              count={healthData.currentMedications.length}
            />
            {expandedSections.medications && (
              <div className="space-y-3 pl-4">
                {healthData.currentMedications.map((med, index) => (
                  <div
                    key={index}
                    className="p-4 border border-gray-200 rounded-lg space-y-3"
                  >
                    <div className="flex justify-between items-start">
                      <h4 className="text-sm font-medium text-gray-700">
                        Medication #{index + 1}
                      </h4>
                      <button
                        type="button"
                        onClick={() => removeMedication(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Medication Name
                        </label>
                        <input
                          type="text"
                          value={med.name}
                          onChange={(e) =>
                            updateMedication(index, "name", e.target.value)
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          placeholder="e.g., metformin"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Dosage
                        </label>
                        <input
                          type="text"
                          value={med.dosage}
                          onChange={(e) =>
                            updateMedication(index, "dosage", e.target.value)
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          placeholder="e.g., 500mg"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Frequency
                        </label>
                        <input
                          type="text"
                          value={med.frequency}
                          onChange={(e) =>
                            updateMedication(index, "frequency", e.target.value)
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          placeholder="e.g., twice daily"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Purpose
                        </label>
                        <input
                          type="text"
                          value={med.purpose}
                          onChange={(e) =>
                            updateMedication(index, "purpose", e.target.value)
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          placeholder="e.g., diabetes"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={med.startDate}
                          onChange={(e) =>
                            updateMedication(index, "startDate", e.target.value)
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Notes
                        </label>
                        <input
                          type="text"
                          value={med.notes}
                          onChange={(e) =>
                            updateMedication(index, "notes", e.target.value)
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          placeholder="e.g., take with meals"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addMedication}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Medication
                </button>
              </div>
            )}
          </div>

          {/* Emergency Information */}
          <div className="space-y-3">
            <SectionHeader
              icon={Shield}
              title="Emergency Information"
              section="emergency"
              count={0}
            />
            {expandedSections.emergency && (
              <div className="space-y-3 pl-4 p-4 border border-gray-200 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={healthData.emergencyInfo.hasEpipen}
                      onChange={(e) =>
                        setHealthData((prev) => ({
                          ...prev,
                          emergencyInfo: {
                            ...prev.emergencyInfo,
                            hasEpipen: e.target.checked,
                          },
                        }))
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Has EpiPen
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Primary Physician
                    </label>
                    <input
                      type="text"
                      value={healthData.emergencyInfo.primaryPhysician}
                      onChange={(e) =>
                        setHealthData((prev) => ({
                          ...prev,
                          emergencyInfo: {
                            ...prev.emergencyInfo,
                            primaryPhysician: e.target.value,
                          },
                        }))
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="Dr. Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Last Checkup
                    </label>
                    <input
                      type="date"
                      value={healthData.emergencyInfo.lastCheckup}
                      onChange={(e) =>
                        setHealthData((prev) => ({
                          ...prev,
                          emergencyInfo: {
                            ...prev.emergencyInfo,
                            lastCheckup: e.target.value,
                          },
                        }))
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={healthData.metadata.sharedWithCoach}
                      onChange={(e) =>
                        setHealthData((prev) => ({
                          ...prev,
                          metadata: {
                            ...prev.metadata,
                            sharedWithCoach: e.target.checked,
                          },
                        }))
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Share with Coach
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-4 sticky bottom-0 bg-white border-t border-gray-200 -mx-5 px-5 py-4">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Health Conditions
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HealthConditions;
